import { useLocation } from 'react-router-dom'
import { FormikProvider, useFormik } from 'formik'

import { STRIPE_CANCELLATION_REASON_KEYS } from '@/actions/settings/billingActions'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useIsDeviceWidth, DeviceWidth } from '@/utils/deviceWidthHelpers'
import { updateAutomaticCancellationAnalyticsRecord } from './lib/userCancellation.slice'
import {
  CANCELLATION_FLOW_STEP,
  useGetSaveMoneyAndTimePageData,
  useSelfServiceCancellationSteps,
} from './lib/helpers'

import { UserCancellationFlowFooter } from './UserCancellationFlowFooter'
import { getFieldName, makeReqStringSchema } from '@/base'
import { TextColors, TypographyStyles } from '@/styles'
import { Form, Image, Separator, Text } from '@/ui'

const cancellationReasonDropdownOptions = [
  {
    key: STRIPE_CANCELLATION_REASON_KEYS.missingFeatures,
    value: STRIPE_CANCELLATION_REASON_KEYS.missingFeatures,
    text: 'I need more features',
  },
  {
    key: STRIPE_CANCELLATION_REASON_KEYS.tooComplex,
    value: STRIPE_CANCELLATION_REASON_KEYS.tooComplex,
    text: 'Ease of use was less than expected',
  },
  {
    key: STRIPE_CANCELLATION_REASON_KEYS.lowQuality,
    value: STRIPE_CANCELLATION_REASON_KEYS.lowQuality,
    text: 'Quality was less than expected',
  },
  {
    key: STRIPE_CANCELLATION_REASON_KEYS.tooExpensive,
    value: STRIPE_CANCELLATION_REASON_KEYS.tooExpensive,
    text: 'It’s too expensive',
  },
  {
    key: STRIPE_CANCELLATION_REASON_KEYS.switchedService,
    value: STRIPE_CANCELLATION_REASON_KEYS.switchedService,
    text: 'I found an alternative',
  },
  {
    key: STRIPE_CANCELLATION_REASON_KEYS.customerService,
    value: STRIPE_CANCELLATION_REASON_KEYS.customerService,
    text: 'Customer service was less than expected',
  },
  {
    key: STRIPE_CANCELLATION_REASON_KEYS.unused,
    value: STRIPE_CANCELLATION_REASON_KEYS.unused,
    text: 'I no longer need it',
  },
  {
    key: STRIPE_CANCELLATION_REASON_KEYS.other,
    value: STRIPE_CANCELLATION_REASON_KEYS.other,
    text: 'Other reasons',
  },
]

export const SavingMoneyAndTime = ({ recordId }: { recordId?: number }) => {
  const location = useLocation()
  const dispatch = useAppDispatch()
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)

  const {
    dollarsSaved,
    financialAccountAlert,
    timeString,
    transactionsCount,
    reconciliationsCount,
    quartersWithEstimates,
    showPersonalizedPage,
  } = useGetSaveMoneyAndTimePageData(recordId)

  const goToNextStep = useSelfServiceCancellationSteps(
    CANCELLATION_FLOW_STEP.savingMoneyAndTime
  )

  const formik = useFormik({
    initialValues: {
      cancellationReason: '',
      cancellationComment: '',
    },
    enableReinitialize: true,
    validateOnMount: true,
    onSubmit: async ({
      cancellationComment,
      cancellationReason,
    }: {
      cancellationComment: string
      cancellationReason: string
    }) => {
      // if they refresh the page, we may lose the record Id since it's saved in state
      if (recordId) {
        await updateAutomaticCancellationAnalyticsRecord(
          location.state.recordId,
          {
            cancellationComment,
            cancellationReason,
          }
        )(dispatch)
      }
      goToNextStep({ cancellationComment, cancellationReason })
    },
  })

  const { isSubmitting, submitForm } = formik

  const hasReconciledAccounts =
    reconciliationsCount !== undefined && reconciliationsCount > 0

  const hasQuartersWithEstimates =
    quartersWithEstimates !== undefined && quartersWithEstimates > 0

  const hasTransactions =
    transactionsCount !== undefined && transactionsCount > 0

  return (
    <div>
      <div className="flex justify-center">
        <Image
          alt="green and yellow hearts"
          src="https://heard-images.s3.amazonaws.com/assets/Support.png"
          height={180}
          width={180}
        />
      </div>

      <div className="flex justify-center">
        <Text variant={TypographyStyles.DISPLAY_LG} textAlign="center">
          We&apos;re sorry to hear you want to leave Heard...
        </Text>
      </div>

      <div className="flex mt-7 gap-6">
        <div className="flex flex-col grow-1 gap-4">
          <Text variant={TypographyStyles.H1}>Saving you money and time</Text>

          <Text variant={TypographyStyles.BODY_LG}>
            We would love to continue our journey together. Before you go, we
            wanted to share some key highlights of your membership.
          </Text>

          {!showPersonalizedPage && (
            <Text variant={TypographyStyles.BODY_LG}>
              On average, our members saved...
            </Text>
          )}
        </div>

        <div className="grow-1">
          <div className="space-y-6">
            <div className="flex">
              <Image
                alt="magnifying glass checkmark"
                src="https://heard-images.s3.amazonaws.com/assets/magnifying-glass-checkmark-2.svg"
                width={80}
                height={80}
                className="w-3/12 h-20 w-20"
              />

              <div className="w-9/12">
                <div className="flex items-end">
                  <Text
                    variant={TypographyStyles.DISPLAY_MD}
                    color={TextColors.GREEN}
                  >
                    {dollarsSaved}
                  </Text>

                  <Text className="ml-1.5 mb-2" color={TextColors.GREEN}>
                    {showPersonalizedPage ? 'saved' : 'avg saved'}
                  </Text>
                </div>

                <Text
                  variant={TypographyStyles.BODY_LG}
                  color={TextColors.MOSS}
                  className="min-h-fit"
                >
                  Identifying and maximizing tax deductions
                </Text>
              </div>
            </div>

            <div className="flex">
              <Image
                alt="clock"
                src="https://heard-images.s3.amazonaws.com/assets/Clock.svg"
                className="w-3/12 h-20 w-20"
              />

              <div className="w-9/12 space-y-2">
                <div className="flex items-end">
                  <Text
                    variant={TypographyStyles.DISPLAY_MD}
                    color={TextColors.GREEN}
                  >
                    {timeString}
                  </Text>

                  <Text className="ml-1.5 mb-2" color={TextColors.GREEN}>
                    {showPersonalizedPage
                      ? 'hours saved'
                      : 'avg hours saved annually'}
                  </Text>
                </div>

                <div className="min-h-fit space-y-1">
                  {hasTransactions && (
                    <Text
                      variant={TypographyStyles.BODY_LG}
                      color={TextColors.MOSS}
                    >
                      {transactionsCount} transactions categorized
                    </Text>
                  )}

                  {hasReconciledAccounts && (
                    <Text
                      variant={TypographyStyles.BODY_LG}
                      color={TextColors.MOSS}
                    >
                      {reconciliationsCount} bank accounts reconciled
                    </Text>
                  )}

                  {showPersonalizedPage ? (
                    hasQuartersWithEstimates && (
                      <Text
                        variant={TypographyStyles.BODY_LG}
                        color={TextColors.MOSS}
                      >
                        {quartersWithEstimates} quarterly tax estimate
                        {quartersWithEstimates > 1 ? 's' : ''}
                      </Text>
                    )
                  ) : (
                    <Text
                      variant={TypographyStyles.BODY_LG}
                      color={TextColors.MOSS}
                    >
                      Ongoing quarterly tax estimates
                    </Text>
                  )}
                </div>
              </div>
            </div>
          </div>

          {financialAccountAlert}
        </div>
      </div>

      <Separator />

      <FormikProvider value={formik}>
        <div className="flex flex-wrap mt-7">
          <Text variant={TypographyStyles.H1} className="w-full md:w-1/2">
            How can we do better?
          </Text>

          <div className={`w-full md:w-1/2 ${isMobile ? 'mt-4' : ''}`}>
            <Form.Select
              label="Why are you thinking of leaving Heard?"
              name={getFieldName<typeof formik.values>('cancellationReason')}
              options={cancellationReasonDropdownOptions}
              placeholder="Select Topic"
              schema={makeReqStringSchema()}
            />

            <Form.Textarea
              name={getFieldName<typeof formik.values>('cancellationComment')}
              placeholder="Let us know what we can do to make Heard better for all therapists"
              label="Can you provide us more details?"
              schema={makeReqStringSchema()}
            />
          </div>
        </div>
      </FormikProvider>

      <UserCancellationFlowFooter
        disabled={isSubmitting}
        stayLabel="Nevermind"
        goToNextStep={submitForm}
      />
    </div>
  )
}
